import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import {
  AutoPolicy,
  GracePeriodDays,
  OldReplaceVehicleQuoteApiRequest,
  Policy,
  RiskModel
} from '@amfam/policy/models';
import { fromVehicleActions, vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { agentQuery } from '@amfam/shared/utility/agent';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, ErrorMsg } from '@amfam/shared/utility/shared-services';
import { ErrorObj, LoadingSpinnerService, ValidationService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';

@Component({
  selector: 'ds-year-vin-lookup',
  templateUrl: './year-vin-lookup.component.html',
  styleUrls: ['./year-vin-lookup.component.scss'],
  standalone: false
})
export class YearVinLookupComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() policyNumber: string;
  @Input() replaceVin: string;

  hasError: Observable<boolean>;
  errorList: Array<ErrorObj> = [];
  errorMessage: string;
  sameVINErrorMessage: string;

  years = [];
  private isAuthorized = true;
  // Form elements
  yearVinForm: UntypedFormGroup;
  yearCtrl: AbstractControl;
  vinCtrl: AbstractControl;
  odometerCtrl: AbstractControl;

  // Grace period
  displayPolicyGracePeriod = false;
  gracePeriodDays$: Observable<string>;
  private policyList: Policy[];
  policy: AutoPolicy;

  // Keeps track of whether an API call is in-flight
  loading: Observable<boolean>;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  private vehicleId: string;
  isSameVin = false;
  private replaceVehicleRisks$: Observable<RiskModel[]>;

  public activeAgents$ = combineLatest(
    this.store.select(fromRoot.getAllActiveAgents),
    this.store.select(agentQuery.getAreAllAgentsLoaded),
    (agents, loaded) => ({ agents, loaded })
  ).pipe(
    filter(data => !!data.loaded),
    map(data => data.agents)
  );

  public getAdvanceAutoPolicies$ = this.store.select(PolicySelectors.selectAdvanceAutoPolicies);
  public userState$ = this.store.select(userQuery.selectUserState);

  constructor(
    private store: Store<fromRoot.RootState>,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    public spinner: LoadingSpinnerService,
    public copyService: CopyService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // Loading indicator
    this.loading = combineLatest(
      this.store.select(vehicleQuery.selectLoading),
      this.store.select(fromRoot.getReplaceVehicleLoading),
      (vehicleSelectLoading, replaceVehicleLoading) => {
        return vehicleSelectLoading || replaceVehicleLoading;
      }
    );

    // Get the risk id
    this.route.params.pipe(take(1)).subscribe(params => {
      this.vehicleId = params['id'];
    });

    // Listen to vehicle select errors
    this.hasError = this.store.select(vehicleQuery.selectHasError);
    this.replaceVehicleRisks$ = this.store.select(fromRoot.getReplaceVehicleRisks).pipe(take(1));

    combineLatest(
      this.store.select(vehicleQuery.selectHasError),
      this.store.select(vehicleQuery.selectStatus),
      (error, status) => {
        return {
          status,
          error
        };
      }
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.error) {
          // Check the state if it has an error before stopping the spinner and displaying a message.
          this.spinner.stop();
          this.errorMessage = this.copyService.get('policy', 'defaultReplaceVehicleErrorMessage');
          if (state.status) {
            this.copyService.get('policy', 'errorMsgs').some((errMsgObj: ErrorMsg) => {
              if (errMsgObj.key === _get(state, 'status.errors[0].message', '')) {
                this.errorMessage = errMsgObj.value;
                return true;
              }
            });
          }
        }
      });

    // Set the years
    this.store
      .select(vehicleQuery.selectYears)
      .pipe(takeUntil(this.stop$))
      .subscribe(years => {
        this.years = years;
      });

    // Listen for valid responses to the year-vin - if we have a valid response, forward to the next route.
    this.store
      .select(vehicleQuery.selectNewVehicle)
      .pipe(
        filter(newVehicle => !!newVehicle),
        take(1)
      )
      .subscribe(newVehicle => {
        if (newVehicle) {
          const odometerValue = _get(this.odometerCtrl, 'value', 0);
          this.store.dispatch(
            fromVehicleActions.SetCriteria({
              year: this.yearCtrl.value,
              vin: this.vinCtrl.value,
              odometer: odometerValue
            })
          );

          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/replace-vehicle/' + this.vehicleId + '/underwriting'],
              extras: { replaceUrl: true }
            })
          );
        }
      });

    combineLatest(
      this.store.select(fromRoot.getReplaceVehicleHasError),
      this.store.select(fromRoot.getReplaceVehicleStatus),
      this.roleService.isAuthorized('replacevehicle_vinyear'),
      (hasError, status, isAuthorized) => ({
        hasError,
        status,
        isAuthorized
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.hasError && state.status) {
          this.store.dispatch(
            fromRouterActions.Go({
              path: ['/policies/risk-selection/grace-period']
            })
          );
          this.store.dispatch(
            new replaceVehicleActions.SendAnalyticsAction({
              pageAnalyticName: 'Error'
            })
          );
        }
        this.isAuthorized = state.isAuthorized;
      });

    this.gracePeriodDays$ = combineLatest([
      this.store.select(PolicySelectors.selectAdvanceAutoPolicies),
      this.store.select(userQuery.selectUserStateCode)
    ]).pipe(
      takeUntil(this.stop$),
      map((advancedAutoPolicies, userState) => {
        const advancedAutoPoliciesLength = _get(advancedAutoPolicies, 'length', 0);
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        return advancedAutoPoliciesLength > 0 || !isExtendedGracePeriodState
          ? GracePeriodDays.REGULAR // 14
          : GracePeriodDays.EXTENDED; // 30
      })
    );

    this.buildForm();

    if (!this.isAuthorized) {
      this.yearVinForm.disable();
    }
  }

  ngAfterViewInit() {
    this.store.dispatch(
      new replaceVehicleActions.SendAnalyticsAction({
        pageAnalyticName: 'VIN:Start',
        eventName: 'start'
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }

  submit() {
    this.spinner.start({
      secondaryMessage: `We’re calculating your quote. This may take a moment.`,
      blockActions: true
    });

    if (_get(this.yearCtrl, 'value') && _get(this.vinCtrl, 'value')) {
      this.store.dispatch(
        fromVehicleActions.LookupVinYear({
          year: this.yearCtrl.value,
          vin: this.vinCtrl.value
        })
      );
    }
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/replace-vehicle']
      })
    );
  }

  private getQuoteRequest(newVehicle: any) {
    const effectiveDate = new Date();
    effectiveDate.setDate(effectiveDate.getDate() + 1);

    const payload: OldReplaceVehicleQuoteApiRequest = {
      policyNumber: this.policyNumber,
      effectiveDate: effectiveDate,
      replaceVIN: this.replaceVin,
      newVehicle: {
        vin: this.vinCtrl.value,
        year: this.yearCtrl.value,
        make: _get(newVehicle, 'make'),
        model: _get(newVehicle, 'model'),
        series: _get(newVehicle, 'series'),
        vehicleType: 'auto'
      }
    };
    this.store.dispatch(new replaceVehicleActions.ReplaceVehicleGetPremiumAction(payload));
  }
  verifyVin() {
    if (this.yearVinForm.controls['vin'].value.length === 17) {
      this.replaceVehicleRisks$.pipe(take(1)).subscribe(vehicle => {
        const matchingVehicle = vehicle.find(vehicleVINRisks => {
          return (
            _get(vehicleVINRisks, 'vin') === this.yearVinForm.controls['vin'].value.toUpperCase()
          );
        });
        if (matchingVehicle) {
          this.isSameVin = true;
          this.sameVINErrorMessage = this.copyService.get(
            'policy',
            'sameVINReplaceVehicleErrorMessage'
          );
        } else {
          this.isSameVin = false;
          this.sameVINErrorMessage = '';
        }
      });
    }
  }

  private buildForm() {
    const formGroup = {
      year: ['unset', Validators.compose([ValidationService.unsetStringValidator])],
      vin: [
        '',
        Validators.compose([
          ValidationService.vinFormatValidator,
          ValidationService.vinRequiredValidator
        ])
      ],
      ...{ odometer: ['', Validators.compose([ValidationService.oneToSevenNumbers])] }
    };
    this.yearVinForm = this.formBuilder.group(formGroup);
    this.yearCtrl = this.yearVinForm.controls['year'];
    this.vinCtrl = this.yearVinForm.controls['vin'];
    this.odometerCtrl = this.yearVinForm.controls['odometer'];
    this.errorList = [];
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = !this.displayPolicyGracePeriod;
  }
}
